import { type ReactNode } from 'react'
import { isDefined, isNotEmpty, type PermissionEntityId, type PermissionType } from '@terros/common'
import { Group } from '@mantine/core'
import { useTranslation } from '@front/model'
import viewportClasses from '../graphViewport/graphViewport.module.css'
import { ZoomControls, useGraphViewport } from '../graphViewport'
import {
  PermGraphCanvas,
  PermGraphLegend,
  toPermGraphDomId,
  type PermGraph,
  type PermGraphNode,
  type PermGraphWinningPath,
} from './graph'

type Props = {
  currentUserId: PermissionEntityId | undefined
  focusedId: PermissionEntityId
  graph: PermGraph
  openId?: PermissionEntityId
  permissionType: PermissionType
  targetId: PermissionEntityId
  winningPath: PermGraphWinningPath
  renderEditor: (node: PermGraphNode) => ReactNode
  onToggle: (node: PermGraphNode) => void
}

export const PermGraphViewport = ({
  currentUserId,
  focusedId,
  graph,
  openId,
  permissionType,
  targetId,
  winningPath,
  renderEditor,
  onToggle,
}: Props): ReactNode => {
  const { t } = useTranslation()
  const { containerRef, contentRef, zoom, pan, isPanning, layoutReady, zoomIn, zoomOut, resetZoom, ...pointer } =
    useGraphViewport({
      focusId: toPermGraphDomId(targetId, permissionType, focusedId),
      hasContent: isNotEmpty(graph.nodes),
      panelOpen: false,
      recenterKey: permissionType,
      revealId: isDefined(openId) ? toPermGraphDomId(targetId, permissionType, openId) : null,
    })

  return (
    <div className={`${viewportClasses.viewport} flex flex-col`}>
      <Group
        align='center'
        className='border-default-border bg-body relative z-10 shrink-0 border-b'
        gap='sm'
        justify='space-between'
        p='sm'
        wrap='wrap'
      >
        <PermGraphLegend />
        <ZoomControls docked zoom={zoom} onZoomIn={zoomIn} onZoomOut={zoomOut} onReset={resetZoom} />
      </Group>
      <div
        ref={containerRef}
        aria-label={t('Permission inheritance viewport')}
        className={`${viewportClasses.viewport} min-h-0`}
        data-panning={isPanning}
        role='region'
        tabIndex={0}
        onLostPointerCapture={pointer.onPointerEnd}
        onPointerCancel={pointer.onPointerEnd}
        onPointerDown={pointer.onPointerDown}
        onPointerMove={pointer.onPointerMove}
        onPointerUp={pointer.onPointerEnd}
      >
        <div
          className={viewportClasses.panLayer}
          style={{
            opacity: layoutReady ? 1 : 0,
            transform: `translate3d(${pan.x}px,${pan.y}px,0)`,
            transition: isPanning ? 'none' : 'transform 200ms ease',
          }}
        >
          <div
            ref={contentRef}
            className={viewportClasses.contentLayer}
            style={{
              transform: `scale(${zoom})`,
              transition: isPanning ? 'none' : 'transform 200ms ease',
            }}
          >
            <PermGraphCanvas
              currentUserId={currentUserId}
              graph={graph}
              openId={openId}
              permissionType={permissionType}
              renderEditor={renderEditor}
              targetId={targetId}
              winningPath={winningPath}
              onToggle={onToggle}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
